/**
 * Hook to handle sending chat messages with Redux-based optimistic updates.
 */

import { useUser } from '@clerk/clerk-react'
import { useSendAgentChatMessageMutation } from '@saaga/shared/store/api.store'
import { selectIsSendingMessage } from '@saaga/shared/store/slices/chat-selectors.store'
import { useAppDispatch, useAppSelector } from '@saaga/shared/store/store'
import { useCallback } from 'react'

import type { UseAgentsWebsocketConfigReturn } from '@/hooks/use-agents-websocket-config.hook'

import { useSendMessageCallback } from './use-chat-send-message-callback.hook'

interface UseMessageSendParams {
  conversationId: string | null
  agencySlug: string | null
  onNewConversation: (newConversationId: string) => void
  websocket?: UseAgentsWebsocketConfigReturn
}

interface UseMessageSendReturn {
  sendMessage: (content: string, attachmentIdList?: string[]) => Promise<void>
  sendNote: (content: string) => Promise<void>
  isSending: boolean
}

export const useMessageSend = (params: UseMessageSendParams): UseMessageSendReturn => {
  const { conversationId, agencySlug, onNewConversation, websocket } = params
  const dispatch = useAppDispatch()
  const { user } = useUser()
  const [sendChatMessage, { isLoading: isHttpSending }] = useSendAgentChatMessageMutation()
  const isReduxSending = useAppSelector(selectIsSendingMessage)

  const send = useSendMessageCallback({
    conversationId,
    agencySlug,
    user,
    dispatch,
    websocket,
    sendChatMessage,
    onNewConversation,
  })

  return {
    sendMessage: useCallback(
      (content: string, attachmentIdList?: string[]) => send(content, false, attachmentIdList),
      [send]
    ),
    sendNote: useCallback((content: string) => send(content, true), [send]),
    isSending: isHttpSending || isReduxSending,
  }
}
